@layer refine.base {
  :host {
    --_backdrop-bg: var(--drawer-backdrop-bg, color-mix(in oklch, var(--color-canvas) 40%, transparent));
    --_backdrop-filter: none;
    --_panel-bg: var(--drawer-bg, var(--color-canvas));
    --_panel-blur: var(--drawer-panel-blur, var(--blur-md));
    --_panel-shadow: var(--drawer-shadow, var(--shadow-xl));
    --_panel-border-color: var(--color-divider);
    --_panel-bleed: var(--size-8);
    --_drag-handle-inset: calc(var(--size-1) * -1);

    display: contents;
  }

  dialog {
    all: unset;
    position: fixed;
    inset: 0;
    box-sizing: border-box;
    display: flex;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    pointer-events: none;
    transition:
      display 200ms allow-discrete,
      overlay 200ms allow-discrete;
  }

  dialog:not([open]) {
    display: none;
  }

  dialog:not([open]) .panel {
    display: none;
  }

  dialog[open] {
    display: flex;
    pointer-events: auto;
  }

  dialog::backdrop {
    background: var(--_backdrop-bg);
    opacity: 1;
    backdrop-filter: var(--_backdrop-filter);
    transition:
      display 200ms allow-discrete,
      overlay 200ms allow-discrete,
      opacity var(--transition-normal),
      backdrop-filter var(--transition-normal);

    @starting-style {
      opacity: 0;
      backdrop-filter: blur(0);
    }
  }

  dialog.closing::backdrop {
    opacity: 0;
    backdrop-filter: blur(0);
    transition:
      opacity 300ms var(--ease-in-out),
      backdrop-filter 300ms var(--ease-in-out),
      overlay 300ms allow-discrete,
      display 300ms allow-discrete;
  }

  .panel {
    position: absolute;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: visible;
    background: var(--_panel-bg);
    border: var(--border) solid var(--_panel-border-color);
    box-shadow: var(--_panel-shadow);
    opacity: 1;
    backdrop-filter: blur(var(--_panel-blur));
    transform: translate3d(0, 0, 0);
    transition:
      opacity var(--transition-normal),
      transform 300ms var(--ease-move);

    @starting-style {
      opacity: 0;
      transform: var(--_panel-exit-transform, translate3d(0, 0, 0));
    }
  }

  dialog.closing .panel {
    opacity: 0;
    transform: var(--_panel-exit-transform, translate3d(0, 0, 0));
  }

  /* left */
  :host([placement='left']) .panel {
    --_panel-exit-transform: translateX(calc(-100% - var(--_panel-bleed)));

    inset-block: 0;
    inset-inline-start: 0;
    width: calc(var(--drawer-size, 20rem) + var(--_panel-bleed));
    max-width: calc(100dvw + var(--_panel-bleed));
    padding-inline-start: var(--_panel-bleed);
    margin-inline-start: calc(var(--_panel-bleed) * -1);
    border-block: none;
    border-inline-start: none;
  }

  /* right (default) */
  :host(:not([placement])) .panel,
  :host([placement='right']) .panel {
    --_panel-exit-transform: translateX(calc(100% + var(--_panel-bleed)));

    inset-block: 0;
    inset-inline-end: 0;
    width: calc(var(--drawer-size, 20rem) + var(--_panel-bleed));
    max-width: calc(100dvw + var(--_panel-bleed));
    padding-inline-end: var(--_panel-bleed);
    margin-inline-end: calc(var(--_panel-bleed) * -1);
    border-block: none;
    border-inline-end: none;
  }

  /* top */
  :host([placement='top']) .panel {
    --_panel-exit-transform: translateY(calc(-100% - var(--_panel-bleed)));

    inset-block-start: 0;
    inset-inline: 0;
    height: calc(var(--drawer-size, 16rem) + var(--_panel-bleed));
    max-height: calc(100dvh + var(--_panel-bleed));
    padding-block-start: var(--_panel-bleed);
    margin-block-start: calc(var(--_panel-bleed) * -1);
    border-block-start: none;
    border-inline: none;
  }

  /* bottom */
  :host([placement='bottom']) .panel {
    --_panel-exit-transform: translateY(calc(100% + var(--_panel-bleed)));

    inset-block-end: 0;
    inset-inline: 0;
    height: calc(var(--drawer-size, 16rem) + var(--_panel-bleed));
    max-height: calc(100dvh + var(--_panel-bleed));
    padding-block-end: var(--_panel-bleed);
    margin-block-end: calc(var(--_panel-bleed) * -1);
    border-block-end: none;
    border-inline: none;
  }

  /* RTL overrides */
  :host(:dir(rtl)[placement='left']) .panel {
    --_panel-exit-transform: translateX(calc(100% + var(--_panel-bleed)));
  }

  :host(:dir(rtl)[placement='right']) .panel,
  :host(:dir(rtl):not([placement])) .panel {
    --_panel-exit-transform: translateX(calc(-100% - var(--_panel-bleed)));
  }

  /* Size utilities */
  :host([size='sm']) {
    --drawer-size: 14rem;
  }

  :host([size='lg']) {
    --drawer-size: 32rem;
  }

  :host([size='full']) {
    --drawer-size: 100%;
  }

  :host([size='full']) .panel {
    width: calc(100% + var(--_panel-bleed));
    max-width: none;
    height: calc(100% + var(--_panel-bleed));
    max-height: none;
    border: none;
  }

  /* Header / Body / Footer */
  .header {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-2);
    align-items: center;
    padding: var(--size-4);
    border-bottom: var(--border) solid var(--color-contrast-200);
  }

  .header-title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: var(--text-lg);
    font-weight: var(--font-semibold);
    white-space: nowrap;
  }

  .body {
    flex: 1;
    padding: var(--size-4);
    overflow-y: auto;
  }

  .footer {
    display: flex;
    flex-shrink: 0;
    gap: var(--size-2);
    justify-content: flex-end;
    padding: var(--size-4);
    border-top: var(--border) solid var(--color-contrast-200);
  }

  .header[hidden],
  .footer[hidden],
  .close-btn[hidden] {
    display: none;
  }

  .close-btn {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: var(--size-8);
    height: var(--size-8);
    margin-inline-start: auto;
    color: var(--color-contrast-500);
    cursor: pointer;
    border-radius: var(--rounded-sm);
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .close-btn:hover {
    color: var(--color-contrast-800);
    background: var(--color-contrast-200);
  }

  .close-btn:active {
    opacity: 0.8;
  }

  /* Drag handle for mobile/touch swipe-to-close */
  .drag-handle {
    position: absolute;
    z-index: 10;
    pointer-events: all;
    touch-action: none;
    cursor: grab;
  }

  .drag-handle:active {
    cursor: grabbing;
  }

  /* Horizontal drawers (left/right) — drag handle on the opposite edge */
  :host([placement='left']) .drag-handle,
  :host(:not([placement])) .drag-handle,
  :host([placement='right']) .drag-handle {
    top: 50%;
    width: var(--_panel-bleed);
    height: 3.5rem;
    translate: 0 -50%;
  }

  /* Left drawer — handle on the right (opposite side) */
  :host([placement='left']) .drag-handle {
    right: calc(var(--_panel-bleed) * -1);
  }

  /* Right drawer (default) — handle on the left (opposite side) */
  :host([placement='right']) .drag-handle,
  :host(:not([placement])) .drag-handle {
    left: calc(var(--_panel-bleed) * -1);
  }

  /* Vertical drawers (top/bottom) — drag handle on the opposite edge */
  :host([placement='top']) .drag-handle,
  :host([placement='bottom']) .drag-handle {
    left: 50%;
    width: 3.5rem;
    height: var(--_panel-bleed);
    translate: -50% 0;
  }

  /* Top drawer — handle at bottom (opposite side) */
  :host([placement='top']) .drag-handle {
    bottom: calc(var(--_panel-bleed) * -1);
  }

  /* Bottom drawer — handle at top (opposite side) */
  :host([placement='bottom']) .drag-handle {
    top: calc(var(--_panel-bleed) * -1);
  }

  /* Inset drag-handle placement keeps the handle fully inside the panel. */
  :host([drag-handle-placement='inset'][placement='left']) .drag-handle {
    right: var(--_drag-handle-inset);
  }

  :host([drag-handle-placement='inset'][placement='right']) .drag-handle,
  :host([drag-handle-placement='inset']:not([placement])) .drag-handle {
    left: var(--_drag-handle-inset);
  }

  :host([drag-handle-placement='inset'][placement='top']) .drag-handle {
    bottom: var(--_drag-handle-inset);
  }

  :host([drag-handle-placement='inset'][placement='bottom']) .drag-handle {
    top: var(--_drag-handle-inset);
  }

  /* Visual indicator — subtle bar in the center */
  .drag-handle::before {
    position: absolute;
    inset: 50%;
    content: '';
    background: var(--color-contrast-400);
    border-radius: var(--rounded-full);
    opacity: 0.6;
    translate: -50% -50%;
    transition:
      background var(--transition-fast),
      opacity var(--transition-fast),
      transform var(--transition-fast);
  }

  :host([placement='left']) .drag-handle::before,
  :host([placement='right']) .drag-handle::before,
  :host(:not([placement])) .drag-handle::before {
    width: 5px;
    height: 2.5rem;
  }

  :host([placement='top']) .drag-handle::before,
  :host([placement='bottom']) .drag-handle::before {
    width: 2.5rem;
    height: 5px;
  }

  .drag-handle:hover::before {
    background: var(--color-contrast-600);
    opacity: 0.9;
    transform: scale(1.15);
  }

  .close-btn:focus-visible {
    outline: var(--focus-ring);
  }

  @media (width <= 640px) {
    :host([placement='left']) .panel,
    :host(:not([placement])) .panel,
    :host([placement='right']) .panel {
      width: calc(min(var(--drawer-size, 20rem), calc(100vw - var(--size-2))) + var(--_panel-bleed));
      max-width: calc(100vw - var(--size-2) + var(--_panel-bleed));
    }

    :host([placement='top']) .panel,
    :host([placement='bottom']) .panel {
      height: calc(min(var(--drawer-size, 16rem), calc(100dvh - var(--size-2))) + var(--_panel-bleed));
      max-height: calc(100dvh - var(--size-2) + var(--_panel-bleed));
    }

    .header,
    .body,
    .footer {
      padding: var(--size-3);
    }
  }
}

@layer refine.config {
  :host([backdrop='blur']) {
    --_backdrop-filter: blur(var(--blur-xs));
  }

  :host([backdrop='transparent']) {
    --_backdrop-bg: transparent;
  }
}
